import styled from "styled-components";

export const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

export const TabRow = styled.div`
  display: flex;
  gap: 8px;
`;

export const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 60px 24px;
  color: ${({ theme }) => theme?.palette?.text?.secondary || "#666"};
`;

export const StatusPill = styled.span`
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  background: ${({ $color, theme }) =>
    $color || theme?.palette?.action?.selected || "#eee"};
  color: ${({ $textColor }) => $textColor || "#222"};
  white-space: nowrap;
`;

export const TitleCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const TitleText = styled.div`
  font-weight: 600;
  font-size: 14px;
  line-height: 1.35;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

export const SubtleText = styled.span`
  color: ${({ theme }) => theme?.palette?.text?.secondary || "#666"};
  font-size: 12px;
`;

/* ── Media-list layout (Direction A): card rows with an inline clip preview ─ */

export const FeedList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  /* CommonPage is a height:100% flex column — claim the remaining space and
     scroll internally so rows below the fold are reachable. */
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 2px 2px 8px;
`;

export const RowWrap = styled.div`
  flex-shrink: 0;
  background: ${({ theme }) => theme?.palette?.background?.paper || "#fff"};
  border: 1px solid ${({ theme }) => theme?.palette?.divider || "rgba(0,0,0,0.10)"};
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  transition: box-shadow 0.15s ease, border-color 0.15s ease;
  overflow: hidden;
  &:hover {
    box-shadow: 0 6px 20px rgba(16, 24, 40, 0.1);
    border-color: ${({ theme }) =>
      theme?.palette?.mode === "dark" ? "rgba(255,255,255,0.18)" : "#d8dce4"};
  }
`;

export const RowMain = styled.div`
  display: flex;
  align-items: stretch;
  gap: 16px;
  padding: 14px;
  min-height: 116px;

  @media (max-width: 768px) {
    gap: 12px;
    padding: 12px;
  }
`;

/* Everything except the thumbnail, as a column: body on top, footer pinned to
   the bottom (space-between) so the footer aligns with the thumbnail's base
   without growing the card. */
export const BodyCol = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 8px;
`;

export const BodyTop = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;

  /* On a phone the title + action buttons can't sit side-by-side without
     crushing the text to a few characters — stack them so the title gets the
     full card width. */
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
  }
`;

/* Footer line: merge pill (left) ←→ show tag + time (right). */
export const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;


/* Inline clip preview — poster frame at rest, plays in place on hover/click. */
export const ThumbWrap = styled.div`
  position: relative;
  width: 170px;
  min-width: 170px;
  aspect-ratio: 16 / 9;
  align-self: center;
  border-radius: 10px;
  overflow: hidden;
  background: #111;
  flex-shrink: 0;
  cursor: pointer;
  @media (max-width: 768px) {
    width: 120px;
    min-width: 120px;
  }
  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

export const ThumbEmpty = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);
  background: linear-gradient(135deg, #2c3038, #1a1d22);
`;

/* Centered play/pause button — a clean custom control (no native chrome /
   overflow menu). Holds an MUI play or pause icon. */
export const ControlButton = styled.div`
  position: absolute;
  inset: 0;
  margin: auto;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  color: #fff;
  transition: background 0.15s ease;
  svg {
    font-size: 28px;
  }
`;

/* Seekable playback scrubber. The track is a tall, transparent hit area so
   clicks reliably land on it (and seek) instead of the video (which would
   pause); the visible line sits slim at the very bottom. */
export const ProgressTrack = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 16px;
  display: flex;
  align-items: flex-end;
  cursor: pointer;
  pointer-events: auto;
`;

export const ProgressLine = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.45);
  transition: height 0.1s ease;
  ${ProgressTrack}:hover & {
    height: 9px;
  }
`;

export const ProgressFill = styled.div`
  height: 100%;
  background: #ff0000;
  transform-origin: left center;
  transition: transform 0.05s linear;
`;

export const DurationBadge = styled.span`
  position: absolute;
  bottom: 7px;
  left: 7px;
  background: rgba(0, 0, 0, 0.78);
  color: #fff;
  font-size: 11.5px;
  font-weight: 700;
  padding: 2px 7px;
  border-radius: 6px;
  letter-spacing: 0.3px;
  direction: ltr;
`;

export const RowText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 6px;
`;

export const FeedTitle = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  font-weight: 700;
  font-size: 16px;
  line-height: 1.35;
  color: ${({ theme }) => theme?.palette?.text?.primary || "#1a1a1a"};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

/* Summary under the title — up to two lines, then ellipsis. Lets editors
   triage without expanding. */
export const Snippet = styled.div`
  font-size: 13px;
  line-height: 1.45;
  color: ${({ theme }) => theme?.palette?.text?.secondary || "#5b6270"};
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const MetaLine = styled.div`
  /* The show chip + date — the right side of the card footer. */
  font-size: 12.5px;
  color: ${({ theme }) => theme?.palette?.text?.secondary || "#6b6b6b"};
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
`;

/* Per-show pill; bg/fg passed in as a stable hue derived from the show name. */
export const ShowChip = styled.span`
  font-size: 12px;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 7px;
  background: ${({ $bg }) => $bg || "#eef3fb"};
  color: ${({ $fg }) => $fg || "#0f4c8a"};
  white-space: nowrap;
`;

export const MetaDot = styled.span`
  opacity: 0.45;
`;

/* Title line wraps the title and (on generated rows) the title-source toggle,
   keeping the toggle right beside the title rather than stranded. */
export const TitleLine = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

/* Engine/candidate title switch — a small flat segmented control beside the
   title. */
export const TitleToggle = styled.div`
  display: inline-flex;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme?.palette?.divider || "rgba(0,0,0,0.14)"};
  border-radius: 7px;
  overflow: hidden;
`;

export const ToggleSeg = styled.button`
  font-family: inherit;
  font-size: 11.5px;
  font-weight: 700;
  line-height: 1.7;
  border: 0;
  padding: 2px 11px;
  cursor: pointer;
  transition: background 0.12s ease, color 0.12s ease;
  background: ${({ $active, theme }) =>
    $active ? theme?.palette?.primary?.main || "#1976d2" : "transparent"};
  color: ${({ $active, theme }) =>
    $active ? "#fff" : theme?.palette?.text?.secondary || "#5b6270"};
  &:hover {
    background: ${({ $active, theme }) =>
      $active
        ? theme?.palette?.primary?.dark || "#1565c0"
        : "rgba(0,0,0,0.045)"};
  }
  & + & {
    border-inline-start: 1px solid
      ${({ theme }) => theme?.palette?.divider || "rgba(0,0,0,0.14)"};
  }
`;

export const RowActions = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  flex-shrink: 0;
  padding-inline-start: 4px;

  /* Once stacked under the title (mobile), sit at the trailing edge. */
  @media (max-width: 768px) {
    align-self: flex-end;
  }
`;

/* "This row is the product of a merge" marker — a calm info pill on the
   footer's left, where the merge warning sits for un-merged rows. Blue/neutral
   (informational), distinct from the amber duplicate warning. */
export const MergedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => (theme?.palette?.mode === "dark" ? "#9ec5ff" : "#15347a")};
  background: ${({ theme }) =>
    theme?.palette?.mode === "dark" ? "rgba(120,170,255,0.14)" : "#eaf1ff"};
  border: 1px solid ${({ theme }) =>
    theme?.palette?.mode === "dark" ? "rgba(120,170,255,0.4)" : "#cfe0ff"};
  border-radius: 8px;
  padding: 4px 10px;
  white-space: nowrap;
  svg {
    font-size: 16px;
  }
`;

/* Possible-duplicate warning — an amber pill on the footer's left. Click to
   expand the review panel below. (Amber = caution, not red/error.) */
export const MergeWarning = styled.button`
  min-width: 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  color: ${({ theme }) => (theme?.palette?.mode === "dark" ? "#ffd479" : "#8a6100")};
  background: ${({ theme }) =>
    theme?.palette?.mode === "dark" ? "rgba(245,180,40,0.14)" : "#fff4d6"};
  border: 1px solid ${({ theme }) =>
    theme?.palette?.mode === "dark" ? "rgba(245,180,40,0.4)" : "#f0d98a"};
  border-radius: 8px;
  padding: 4px 8px 4px 10px;
  cursor: pointer;
  transition: background 0.12s ease;
  &:hover {
    background: ${({ theme }) =>
      theme?.palette?.mode === "dark" ? "rgba(245,180,40,0.22)" : "#ffedb8"};
  }
  svg {
    font-size: 16px;
  }
  .chev {
    transition: transform 0.18s ease;
  }
  .chev.open {
    transform: rotate(180deg);
  }
`;

/* Review panel revealed under the card when the warning is expanded — the
   suggested clip to merge, so the editor can look before acting. */
export const MergePanel = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin: 0 14px 14px;
  padding: 12px;
  border-radius: 12px;
  background: ${({ theme }) =>
    theme?.palette?.mode === "dark" ? "rgba(245,180,40,0.08)" : "#fffaee"};
  border: 1px solid ${({ theme }) =>
    theme?.palette?.mode === "dark" ? "rgba(245,180,40,0.3)" : "#f0e2b8"};
  min-height: 96px;
`;

export const MergePanelInfo = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  .ptitle {
    font-size: 14px;
    font-weight: 700;
    line-height: 1.35;
    color: ${({ theme }) => theme?.palette?.text?.primary || "#1a2433"};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .psummary {
    font-size: 12.5px;
    line-height: 1.45;
    color: ${({ theme }) => theme?.palette?.text?.secondary || "#5b6270"};
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
  .pmeta {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    font-size: 12.5px;
    color: ${({ theme }) => theme?.palette?.text?.secondary || "#5b6270"};
  }
`;
